import { Box, Stack } from '@mui/material';
import { useCallback, useId, useRef, useState } from 'react';

import { SplitButton } from '@/components/form/baseInputs/SplitButton/SplitButton';
import { Switch } from '@/components/form/baseInputs/Switch';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { PopperContent } from '@/components/poppers/PopperContent';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';

import { EditTestamentaryEntitySection } from '../EditTestamentaryEntitySplitScreenModal';
import { EditTestamentaryEntitySplitScreenModal } from '../EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreenModal';
import { useCreateTestamentaryEntityMutation } from '../graphql/CreateTestamentaryEntity.generated';
import {
  CREATE_TESTAMENTARY_ENTITY_SHORT_FORM,
  CreateTestamentaryEntityModalProps,
  CreateTestamentaryEntityShortFormShape,
} from './CreateTestamentaryEntityModal.types';
import {
  getDefaultValues,
  mapFormDataToMutationInput,
} from './CreateTestamentaryEntityModal.utils';
import { CreateTestamentaryEntityShortForm } from './CreateTestamentaryEntityShortForm';

interface CreateTestamentaryEntityModalInnerProps
  extends CreateTestamentaryEntityModalProps {
  areAllClientsDead: boolean;
}

function CreateTestamentaryEntityModalInner({
  householdId,
  onClose,
  heading = 'Create sub-trust or inherited account',
  deadClientIds,
  isTwoClientHousehold,
  areAllClientsDead,
  onAfterCreate,
  ignoredQueryDocuments,
  defaultEstateTaxStatus,
  ...formModalProps
}: CreateTestamentaryEntityModalInnerProps) {
  const buttonId = useId();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const editEntityImmediately = useRef<boolean>(false);

  // State for managing the edit modal
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [shouldCreateAdditionalEntities, setShouldCreateAdditionalEntities] =
    useState(false);

  const {
    handleSubmit,
    reset,
    setFocus,
    shouldBlockNavigation,
    formState: { isSubmitting },
  } = useFormContext<CreateTestamentaryEntityShortFormShape>();

  const { formRef, handleSave } = useFormSaveHandler();

  const [createMutation, { data: createdEntityData }] =
    useCreateTestamentaryEntityMutation({
      refetchQueries: 'active',
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError('could not create testamentary entity', error);
      },
      onQueryUpdated: (query) =>
        shouldRefetchQuery(query.queryName, { ignoredQueryDocuments }),
    });

  const onSubmit = handleSubmit(async (formData) => {
    const input = mapFormDataToMutationInput(formData, householdId);
    await createMutation({ variables: { input } });
  });

  const closeModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmitSuccess = useCallback(() => {
    if (!createdEntityData) return;

    showFeedback('Sub-trust created successfully', { variant: 'success' });

    if (shouldCreateAdditionalEntities && !editEntityImmediately.current) {
      setFocus(`${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.displayName`);
      reset(
        getDefaultValues({
          areAllClientsDead,
          defaultEstateTaxStatus,
        })
      );
      return;
    }

    if (editEntityImmediately.current) {
      setCreatedEntityId(createdEntityData.createTestamentaryEntity.id);
      setIsEditModalOpen(true);
    } else {
      closeModal();
      onAfterCreate?.({
        id: createdEntityData.createTestamentaryEntity.id,
        displayName: createdEntityData.createTestamentaryEntity.displayName,
      });
    }
  }, [
    createdEntityData,
    showFeedback,
    shouldCreateAdditionalEntities,
    setFocus,
    reset,
    areAllClientsDead,
    defaultEstateTaxStatus,
    closeModal,
    onAfterCreate,
  ]);

  useSubmitSuccessHandler(handleSubmitSuccess);

  return (
    <>
      <FormModal
        {...formModalProps}
        onClose={closeModal}
        heading={heading}
        shouldBlockClose={shouldBlockNavigation}
        data-testid="CreateTestamentaryEntityModal"
        actions={
          <Stack
            direction="row"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Switch
                labelPosition="right"
                label="Create additional sub-trusts"
                contextualHelp={
                  <PopperContent body="Enable this toggle if you'd like to create multiple sub-trusts in quick succession" />
                }
                value={shouldCreateAdditionalEntities}
                onChange={(event) =>
                  setShouldCreateAdditionalEntities(event.target.checked)
                }
              />
            </Box>
            <Stack direction="row" spacing={1}>
              <FormModalActions.CancelButton onClick={closeModal} />
              <SplitButton
                htmlId={buttonId}
                onClick={() => {
                  // Reset in case a previous "Input additional details" attempt failed
                  editEntityImmediately.current = false;
                  handleSave();
                }}
                loading={isSubmitting}
                buttonContent="Create sub-trust"
                mainButtonTestId="CreateTestamentaryEntityModal-create"
                caretButtonTestId="CreateTestamentaryEntityModal-options"
                size="sm"
                variant="primary"
                items={[
                  {
                    name: 'Input additional details for this entity',
                    clickHandler: () => {
                      editEntityImmediately.current = true;
                      handleSave();
                    },
                  },
                ]}
              />
            </Stack>
          </Stack>
        }
      >
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <CreateTestamentaryEntityShortForm
            areAllClientsDead={areAllClientsDead}
            defaultEstateTaxStatus={defaultEstateTaxStatus}
            householdId={householdId}
          />
        </form>
      </FormModal>

      {isEditModalOpen && createdEntityId && (
        <EditTestamentaryEntitySplitScreenModal
          isOpen={isEditModalOpen}
          testamentaryEntityId={createdEntityId}
          isTwoClientHousehold={isTwoClientHousehold}
          initialSection={EditTestamentaryEntitySection.BASIC_INFORMATION}
          onClose={() => {
            setIsEditModalOpen(false);
            closeModal();
            onAfterCreate?.({
              id: createdEntityData!.createTestamentaryEntity.id,
              displayName:
                createdEntityData!.createTestamentaryEntity.displayName,
            });
          }}
        />
      )}
    </>
  );
}

export function CreateTestamentaryEntityModal(
  props: CreateTestamentaryEntityModalProps
) {
  const {
    deadClientIds,
    isTwoClientHousehold,
    defaultEstateTaxStatus,
    defaultName,
    defaultKind,
  } = props;

  const areAllClientsDead = isTwoClientHousehold
    ? deadClientIds.length === 2
    : true;

  const formMethods = useForm<CreateTestamentaryEntityShortFormShape>({
    defaultValues: getDefaultValues({
      areAllClientsDead,
      defaultEstateTaxStatus,
      defaultName,
      defaultKind,
    }),
  });

  return (
    <FormProvider {...formMethods}>
      <CreateTestamentaryEntityModalInner
        {...props}
        areAllClientsDead={areAllClientsDead}
      />
    </FormProvider>
  );
}
